import EventManager from 'tools/event-manager';
import Bus from 'tools/bus';

class SharedEvents {
    /**
     * 
     * @param selector - The selector to find the elements that should share a state.
     * 
     * @param eventActions - Should be an object with key/value pairs where the key is the event to listen for, and
     * the value is a callback that will be executed for each element in this instance with the element in question as
     * the first parameter, and the event as the second parameter. A null handler may be passed so that you collectively
     * listen to an event from all elements.
     * 
     * For example:
     * 
     * new SharedState('.button', {
     *     mouseenter: (element, event) => {
     *         element.classList.add('hover');
     *     },
     *     mouseleave: (element, event) => {
     *         element.classList.remove('hover');
     *     },
     *     click: null
     * });
     * 
     * or:
     * 
     * function handler(element, event) {
     *     let method = event.type === 'mouseenter' ? 'add' : 'remove';
     *     
     *     element.classList[method]('hover');
     * }
     * 
     * new SharedState('.button', {
     *     mouseenter: handler,
     *     mouseleave: handler
     * });
     * 
     */
    constructor(selector, eventActions) {
        this.events = new EventManager({
            bind: this
        });
        
        // Allow single event to be set on this instance that will be triggered when any of the elements with share
        // state receive the specified event.
        this.bus = new Bus(this);
        
        this.selector = selector;
        this.eventActions = eventActions;
        this.elements = [ ];
        
        this.update();
    }

    /**
     * Cache elements
     */
    cacheElements() {
        this.elements = Array.from(document.querySelectorAll(this.selector));
    }

    /**
     * Attach events
     */
    attachEvents() {
        Object.keys(this.eventActions).forEach(event => {
            this.elements.forEach(element => {
                this.events.on(element, event, this.onEvent);
            })
        });
    }

    /**
     * Listner for events set on matching elements.
     * 
     * @param event
     */
    onEvent(event) {
        this.elements.forEach(element => {
            if (this.eventActions[event.type]) {
                this.eventActions[event.type](element, event);
            }
        });
        
        this.bus.emit(event.type, this);
    }

    /**
     * Reset and start again.
     */
    update() {
        this.events.off();
        
        this.cacheElements();
        this.attachEvents();
    }
}

export default SharedEvents;